<template>
    <component
        ref="elImg"
        :is="cmp"
        v-bind="$attrs"
        :src="imgSrc"
        :srcset="imgSrcSet"
        :alt="altText"
        :sizes="sizes"
        itemprop="image"
        class="products-image"
        @load="onLoadSuccess"
        @error="onLoadError"
        :class="{
            'sku-img-filter-1': (!!imageObject?.showDropShadow && !disableDropShadowOverride),
            'data-uri-image': isNonImage
        }"
    />
</template>

<script lang="ts" setup>
    import type { FullImage } from '~/types';
    import { type ImageSizeTypes } from '~/models';
    import { useFullImageSetup } from '~/composables';
    import { VImg } from 'vuetify/components';

    const {
        imageObject,
        type = 'medium',
        disableDropShadow = false,
        tag = 'img'
    } = defineProps<{
        imageObject?: Nilish<FullImage>;
        type?: Nilish<ImageSizeTypes | ImageSizeTypes[] | 'ALL'>;
        disableDropShadow?: boolean;
        scalable?: boolean;
        tag?: Nilish<'img' | 'VImg'>;
    }>();

    const emit = defineEmits<{
        'on-load-init': [value: void];
        'on-load-start': [value: void];
        'on-load-end': [value: void];
        'on-load-error': [value: void];
    }>();

    const cmp = shallowRef(tag === 'img' ? 'img' : VImg);
    const elImg = shallowRef<VImg | HTMLImageElement>();

    const {
        init,
        imgSrc,
        imgSrcSet,
        altText,
        sizes,
        disableDropShadowOverride,
        onError,
        isNonImage
    } = useFullImageSetup({
        imageObject: imageObject,
        type: type === 'ALL' ? null : type,
        disableDropShadow: disableDropShadow
    });

    init();

    const isLoaded = ref(false);
    emit('on-load-start');

    function getElObj(): HTMLImageElement | undefined {
        return elImg.value && '$el' in elImg.value ? elImg.value.$el : elImg.value;
    }

    function onLoadSuccess(e: Event): void {
        isLoaded.value = true;
        emit('on-load-end');
    }

    function onLoadError(e: Event): void {
        isLoaded.value = true;
        emit('on-load-error');
        onError(e);
    }

    onMounted(() => {
        if (isNonImage.value || getElObj()?.complete) {
            emit('on-load-end');
        }
    });

    onBeforeUnmount(() => {
        emit('on-load-end');
    });
</script>

<style lang="scss"></style>
